import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import React, { FC, useMemo } from 'react';
import { useLocation } from 'react-router';
import Icon from '~ComponentsImproved/Icon';
import ArrowForwardIcon from '~Assets/icons/arrow_forward.svg';
import { useButton, useCurrency } from '~Hooks';
import useI18n from '~Translations';
import { ChequeWithTableCode } from './HistoryOrdersList';

const OrderRowContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 8px 16px;
  text-transform: none;
  margin-bottom: 8px;
  height: 48px;
  box-sizing: border-box;
  align-items: center;
  position: relative;
  overflow: hidden;

  &:active {
    background-color: var(--colorBlueGray900);
  }

  border: ${({ isSelected }) =>
    isSelected ? '1px solid var(--colorBlueGray700)' : '1px solid var(--colorBlueGray1100)'};
`;

const OrderRowDetails = styled.div`
  color: var(--colorWhite);
  display: flex;
  font-weight: 600;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: calc(100% - 30px);
`;

const OrderColor = styled.div<{ color: string }>`
  position: absolute;
  left: 0;
  height: 100%;
  top: 0;
  width: 4px;
  background-color: ${({ color }) => color};
`;

interface ChequeRowProps {
  cheque: ChequeWithTableCode;
  onChequeClick: () => void;
}

/**
 * Individual order row component that manages its own selection state
 * by reading the orderId from the URL
 */
const ChequeRow: FC<ChequeRowProps> = ({ cheque, onChequeClick }) => {
  const currency = useCurrency();
  const location = useLocation();
  const { i18n } = useI18n();

  // Extract the selected order ID from URL params
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedOrderId = searchParams.get('chequeId');

  // Check if this specific order is selected
  const isSelected = selectedOrderId === cheque.id;

  // Handle order click - update URL with orderId parameter
  const rowButton = useButton({
    onClick: onChequeClick,
  });

  return (
    <OrderRowContainer {...rowButton} isSelected={isSelected}>
      <OrderColor color="var(--colorBlueGray300)" />
      <OrderRowDetails>
        <span>{i18n('History.Table') + ' ' + cheque.tableCode}</span>
        <span>{DateTime.fromISO(cheque.createdAt!).toFormat('HH:mm dd/MM')}</span>
        <span>{currency.format(cheque.paidAmount)}</span>
      </OrderRowDetails>
      {isSelected && <Icon src={ArrowForwardIcon} color="var(--colorWhite)" height="24px" />}
    </OrderRowContainer>
  );
};

export default ChequeRow;
